import React, { useState, useEffect, useMemo } from 'react';
import {
  Calendar, Download, Filter, RefreshCw, Loader2, Clock, User,
  AlertTriangle, CheckCircle, XCircle, FileSpreadsheet, FileText,
  Building2, ChevronDown, CalendarDays, Calculator, FileDown
} from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { departmentService, employeeService } from '../services';
import authService from '../services/authService';
import { useAppContext } from '../context/AppContext';
import { Role } from '../types';
import SiteLocationFilter from '../components/SiteLocationFilter';

const MONTH_KEYS = [
  'months.january', 'months.february', 'months.march', 'months.april',
  'months.may', 'months.june', 'months.july', 'months.august',
  'months.september', 'months.october', 'months.november', 'months.december'
];

// Types
interface DailyTimesheetRow {
  kartNo: string;
  isim: string;
  grup: string;
  bolum: string;
  vardiya: string;
  tarih: string;
  giris1: string | null;
  cikis1: string | null;
  giris2: string | null;
  cikis2: string | null;
  giris3: string | null;
  cikis3: string | null;
  giris4: string | null;
  cikis4: string | null;
  normalCalisma: string | null;
  fazlaMesai50: string | null;
  fazlaMesai100: string | null;
  devamsizlik: string | null;
  gecKalma: string | null;
  erkenCikma: string | null;
  eksikSure: string | null;
  ucretliIzin: number;
  yillikIzin: number;
  ucretsizIzin: number;
  aciklama: string;
}

interface DailyTimesheetSummary {
  toplamPersonel: number;
  devamEden: number;
  devamsiz: number;
  gecKalan: number;
  erkenCikan: number;
  mesaiYapan?: number;
  toplamCalisma: string;
  toplamMesai: string;
  toplamDevamsizlik: string;
}

interface DailyTimesheetReport {
  filter: {
    baslangicTarihi: string;
    bitisTarihi: string;
    departmentId?: number;
    employeeId?: number;
    durum?: string;
  };
  rows: DailyTimesheetRow[];
  summary: DailyTimesheetSummary;
  generatedAt: string;
}

const API_BASE = `${import.meta.env.VITE_API_URL || ''}/api/timesheetapi`;

const DailyTimesheet = () => {
  const { t } = useTranslation();
  const { currentUser } = useAppContext();
  const isDeptManager = currentUser.role === Role.DEPARTMENT_MANAGER;
  const storedUser = authService.getStoredUser();
  const [searchParams] = useSearchParams();
  const urlEmployeeId = searchParams.get('employeeId');

  // State
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState<DailyTimesheetReport | null>(null);
  const [departments, setDepartments] = useState<{ id: number; name: string }[]>([]);
  const [employees, setEmployees] = useState<{ id: number; name: string; departmentId: number }[]>([]);
  const [employeeId, setEmployeeId] = useState<number | ''>(() => urlEmployeeId ? Number(urlEmployeeId) : '');
  const [autoLoadTriggered, setAutoLoadTriggered] = useState(false);

  // Filters - departman yoneticisi icin departmentId otomatik ayarlanir
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(1); // Ayin 1'i
    return d.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [departmentId, setDepartmentId] = useState<number | ''>(() => {
    if (isDeptManager && storedUser?.departmentID) return storedUser.departmentID;
    return '';
  });
  const [durum, setDurum] = useState('Tumu');
  const [siteId, setSiteId] = useState<string>('all');
  const [locationId, setLocationId] = useState<string>('all');

  // URL'den employeeId geldiyse otomatik rapor yukle
  useEffect(() => {
    if (urlEmployeeId && employees.length > 0 && !autoLoadTriggered) {
      setAutoLoadTriggered(true);
      loadReport();
    }
  }, [employees, urlEmployeeId, autoLoadTriggered]);

  // Load departments & employees
  useEffect(() => {
    loadDepartments();
    loadEmployees();
  }, []);

  const loadDepartments = async () => {
    try {
      const res = await departmentService.getAll();
      if (res.success && res.data) {
        setDepartments(res.data.map((d: any) => ({ id: d.id || d.departmentId, name: d.name || d.departmentName })));
      }
    } catch (error) {
      console.error('Departmanlar yuklenirken hata:', error);
    }
  };

  const loadEmployees = async () => {
    try {
      const res = await employeeService.getAll(true);
      if (res.success && res.data) {
        setEmployees(res.data.map((e: any) => ({
          id: e.employeeId || e.id,
          name: `${e.firstName || ''} ${e.lastName || ''}`.trim(),
          departmentId: e.departmentId || e.departmentID || 0
        })));
      }
    } catch (error) {
      console.error('Personeller yuklenirken hata:', error);
    }
  };

  // Departman secildiginde personel filtreleme
  const filteredEmployees = departmentId
    ? employees.filter(e => e.departmentId === departmentId)
    : employees;

  const loadReport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        startDate,
        endDate,
        durum
      });
      if (departmentId) params.append('departmentId', departmentId.toString());
      if (employeeId) params.append('employeeId', employeeId.toString());
      if (siteId !== 'all') params.append('siteId', siteId);
      if (locationId !== 'all') params.append('locationId', locationId);

      const response = await fetch(`${API_BASE}/daily?${params}`);
      if (response.ok) {
        const data = await response.json();
        setReport(data);
      } else {
        console.error('Rapor yuklenemedi');
      }
    } catch (error) {
      console.error('Rapor yuklenirken hata:', error);
    } finally {
      setLoading(false);
    }
  };

  const buildExportParams = () => {
    const params = new URLSearchParams({ startDate, endDate, durum });
    if (departmentId) params.append('departmentId', departmentId.toString());
    if (employeeId) params.append('employeeId', employeeId.toString());
    return params;
  };

  const exportToExcel = () => {
    window.location.href = `${API_BASE}/export/excel?${buildExportParams()}`;
  };

  const exportToText = () => {
    window.location.href = `${API_BASE}/export/text?${buildExportParams()}`;
  };

  const exportToPdf = () => {
    const params = buildExportParams();

    window.location.href = `${API_BASE}/export/pdf?${params}`;
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('tr-TR');
  };

  const formatTime = (timeStr: string | null) => {
    if (!timeStr) return '-';
    // "07:30:00" -> "07:30"
    return timeStr.substring(0, 5);
  };

  const getRowClass = (row: DailyTimesheetRow) => {
    if (row.devamsizlik && row.devamsizlik !== '00:00:00') return 'bg-red-50';
    if (row.gecKalma && row.gecKalma !== '00:00:00') return 'bg-amber-50';
    if (row.eksikSure && row.eksikSure !== '00:00:00') return 'bg-orange-50';
    return '';
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      {/* Header */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
        <div>
          <h1 className="text-2xl font-black text-slate-800 tracking-tight flex items-center gap-3">
            <Calculator className="text-purple-600" size={28} />
            {t('dailyTimesheet.title')}
          </h1>
          <p className="text-slate-500 text-sm font-medium">{t('dailyTimesheet.subtitle')}</p>
        </div>
        <div className="flex gap-3 w-full lg:w-auto">
          <button
            onClick={loadReport}
            disabled={loading}
            className="flex-1 lg:flex-none bg-purple-600 text-white px-5 py-2.5 rounded-xl text-sm font-bold flex items-center justify-center gap-2 hover:bg-purple-700 shadow-xl shadow-purple-100 transition-all active:scale-95 disabled:opacity-50"
          >
            {loading ? <Loader2 size={18} className="animate-spin" /> : <Calculator size={18} />}
            {t('dailyTimesheet.calculate')}
          </button>
          {report && report.rows.length > 0 && (
            <>
              <button
                onClick={exportToExcel}
                className="flex-1 lg:flex-none bg-green-600 text-white px-5 py-2.5 rounded-xl text-sm font-bold flex items-center justify-center gap-2 hover:bg-green-700 shadow-xl shadow-green-100 transition-all active:scale-95"
              >
                <FileSpreadsheet size={18} /> Excel
              </button>
              <button
                onClick={exportToPdf}
                className="flex-1 lg:flex-none bg-red-600 text-white px-5 py-2.5 rounded-xl text-sm font-bold flex items-center justify-center gap-2 hover:bg-red-700 shadow-xl shadow-red-100 transition-all active:scale-95"
              >
                <FileDown size={18} /> PDF
              </button>
            </>
          )}
        </div>
      </div>

      {/* Quick Date Filters */}
      <div className="bg-white rounded-[2rem] shadow-sm border border-slate-200 p-4">
        <div className="flex flex-wrap gap-2 items-center">
          <span className="text-xs font-bold text-slate-500 mr-2">{t('dailyTimesheet.quickSelect')}</span>
          <button
            onClick={() => {
              const today = new Date();
              setStartDate(today.toISOString().split('T')[0]);
              setEndDate(today.toISOString().split('T')[0]);
            }}
            className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-xs font-bold transition-colors"
          >
            {t('dailyTimesheet.today')}
          </button>
          <button
            onClick={() => {
              const today = new Date();
              const yesterday = new Date(today);
              yesterday.setDate(yesterday.getDate() - 1);
              setStartDate(yesterday.toISOString().split('T')[0]);
              setEndDate(yesterday.toISOString().split('T')[0]);
            }}
            className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-xs font-bold transition-colors"
          >
            {t('dailyTimesheet.yesterday')}
          </button>
          <button
            onClick={() => {
              const today = new Date();
              const last7 = new Date(today);
              last7.setDate(last7.getDate() - 6);
              setStartDate(last7.toISOString().split('T')[0]);
              setEndDate(today.toISOString().split('T')[0]);
            }}
            className="px-3 py-1.5 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-lg text-xs font-bold transition-colors"
          >
            {t('dailyTimesheet.last7Days')}
          </button>
          <button
            onClick={() => {
              const today = new Date();
              const last15 = new Date(today);
              last15.setDate(last15.getDate() - 14);
              setStartDate(last15.toISOString().split('T')[0]);
              setEndDate(today.toISOString().split('T')[0]);
            }}
            className="px-3 py-1.5 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-lg text-xs font-bold transition-colors"
          >
            {t('dailyTimesheet.last15Days')}
          </button>
          <button
            onClick={() => {
              const today = new Date();
              const last30 = new Date(today);
              last30.setDate(last30.getDate() - 29);
              setStartDate(last30.toISOString().split('T')[0]);
              setEndDate(today.toISOString().split('T')[0]);
            }}
            className="px-3 py-1.5 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-lg text-xs font-bold transition-colors"
          >
            {t('dailyTimesheet.last30Days')}
          </button>
          <div className="h-6 w-px bg-slate-200 mx-2" />
          <button
            onClick={() => {
              const today = new Date();
              const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
              setStartDate(firstDay.toISOString().split('T')[0]);
              setEndDate(today.toISOString().split('T')[0]);
            }}
            className="px-3 py-1.5 bg-green-100 hover:bg-green-200 text-green-700 rounded-lg text-xs font-bold transition-colors"
          >
            {t('dailyTimesheet.thisMonth')}
          </button>
          <button
            onClick={() => {
              const today = new Date();
              const firstDay = new Date(today.getFullYear(), today.getMonth() - 1, 1);
              const lastDay = new Date(today.getFullYear(), today.getMonth(), 0);
              setStartDate(firstDay.toISOString().split('T')[0]);
              setEndDate(lastDay.toISOString().split('T')[0]);
            }}
            className="px-3 py-1.5 bg-green-100 hover:bg-green-200 text-green-700 rounded-lg text-xs font-bold transition-colors"
          >
            {t('dailyTimesheet.lastMonth')}
          </button>
          <div className="h-6 w-px bg-slate-200 mx-2" />
          <select
            onChange={(e) => {
              if (e.target.value) {
                const monthIndex = parseInt(e.target.value);
                const today = new Date();
                const year = today.getFullYear();
                const firstDay = new Date(year, monthIndex, 1);
                const lastDay = new Date(year, monthIndex + 1, 0);
                setStartDate(firstDay.toISOString().split('T')[0]);
                setEndDate(lastDay.toISOString().split('T')[0]);
                e.target.value = '';
              }
            }}
            className="px-3 py-1.5 bg-amber-100 hover:bg-amber-200 text-amber-700 rounded-lg text-xs font-bold transition-colors border-0 cursor-pointer"
            defaultValue=""
          >
            <option value="" disabled>{t('dailyTimesheet.selectMonth')}</option>
            {MONTH_KEYS.map((key, idx) => (
              <option key={idx} value={idx}>{t(key)}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-[2rem] shadow-sm border border-slate-200 p-6">
        <div className="flex flex-wrap gap-4 items-end">
          <div>
            <label className="block text-xs font-bold text-slate-500 mb-1.5">{t('dailyTimesheet.startDate')}</label>
            <div className="relative">
              <Calendar size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-500 mb-1.5">{t('dailyTimesheet.endDate')}</label>
            <div className="relative">
              <Calendar size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-500 mb-1.5">{t('dailyTimesheet.department')}</label>
            <div className="relative">
              <Building2 size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <select
                value={departmentId}
                onChange={(e) => { if (!isDeptManager) { setDepartmentId(e.target.value ? Number(e.target.value) : ''); setEmployeeId(''); } }}
                disabled={isDeptManager}
                className={`pl-10 pr-8 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium appearance-none min-w-[200px] focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none ${isDeptManager ? 'opacity-60 cursor-not-allowed' : ''}`}
              >
                {!isDeptManager && <option value="">{t('dailyTimesheet.allDepartments')}</option>}
                {departments.map(d => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </select>
              <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-500 mb-1.5">{t('dailyTimesheet.employee')}</label>
            <div className="relative">
              <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <select
                value={employeeId}
                onChange={(e) => setEmployeeId(e.target.value ? Number(e.target.value) : '')}
                className="pl-10 pr-8 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium appearance-none min-w-[220px] focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
              >
                <option value="">{t('dailyTimesheet.allEmployees')}</option>
                {filteredEmployees.map(e => (
                  <option key={e.id} value={e.id}>{e.name}</option>
                ))}
              </select>
              <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-500 mb-1.5">{t('dailyTimesheet.status')}</label>
            <div className="relative">
              <Filter size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <select
                value={durum}
                onChange={(e) => setDurum(e.target.value)}
                className="pl-10 pr-8 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium appearance-none min-w-[140px] focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
              >
                <option value="Tumu">{t('dailyTimesheet.statusAll')}</option>
                <option value="Aktif">{t('dailyTimesheet.statusActive')}</option>
                <option value="Pasif">{t('dailyTimesheet.statusInactive')}</option>
              </select>
              <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            </div>
          </div>
          {/* Ag 2026: Şantiye + Lokasyon filtresi */}
          <div className="col-span-2">
            <label className="block text-xs font-bold text-slate-500 mb-1.5">Şantiye / Lokasyon</label>
            <SiteLocationFilter
              selectedSiteId={siteId}
              selectedLocationId={locationId}
              onSiteChange={setSiteId}
              onLocationChange={setLocationId}
            />
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      {report && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <div className="bg-slate-900 rounded-2xl p-5 text-white">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center">
                <User size={20} className="text-blue-400" />
              </div>
              <div>
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">{t('dailyTimesheet.summaryTotal')}</p>
                <h3 className="text-xl font-black">{report.summary.toplamPersonel}</h3>
              </div>
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-50 rounded-xl flex items-center justify-center">
                <CheckCircle size={20} className="text-green-600" />
              </div>
              <div>
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">{t('dailyTimesheet.summaryAttending')}</p>
                <h3 className="text-xl font-black text-green-600">{report.summary.devamEden}</h3>
              </div>
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-50 rounded-xl flex items-center justify-center">
                <XCircle size={20} className="text-red-600" />
              </div>
              <div>
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">{t('dailyTimesheet.summaryAbsent')}</p>
                <h3 className="text-xl font-black text-red-600">{report.summary.devamsiz}</h3>
              </div>
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-50 rounded-xl flex items-center justify-center">
                <AlertTriangle size={20} className="text-amber-600" />
              </div>
              <div>
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">{t('dailyTimesheet.summaryLate')}</p>
                <h3 className="text-xl font-black text-amber-600">{report.summary.gecKalan}</h3>
              </div>
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-orange-50 rounded-xl flex items-center justify-center">
                <Clock size={20} className="text-orange-600" />
              </div>
              <div>
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">{t('dailyTimesheet.summaryEarlyLeave')}</p>
                <h3 className="text-xl font-black text-orange-600">{report.summary.erkenCikan}</h3>
              </div>
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center">
                <Clock size={20} className="text-blue-600" />
              </div>
              <div>
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">MESAI YAPAN</p>
                <h3 className="text-xl font-black text-blue-600">{report.summary.mesaiYapan ?? 0}</h3>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-[2rem] shadow-sm border border-slate-200 overflow-hidden">
        <div className="px-8 py-5 border-b border-slate-100 flex justify-between items-center">
          <h2 className="text-lg font-black text-slate-800">
            {t('dailyTimesheet.resultsTitle')}
            {report && <span className="text-slate-400 font-medium ml-2">({report.rows.length} {t('dailyTimesheet.records')})</span>}
          </h2>
        </div>

        {loading ? (
          <div className="py-20 text-center">
            <Loader2 size={48} className="mx-auto text-purple-500 animate-spin mb-4" />
            <p className="text-slate-400 font-bold">{t('dailyTimesheet.calculating')}</p>
          </div>
        ) : !report ? (
          <div className="py-20 text-center">
            <Calculator size={48} className="mx-auto text-slate-200 mb-4" />
            <h3 className="text-slate-400 font-bold uppercase text-xs tracking-wider">{t('dailyTimesheet.selectDateRange')}</h3>
            <p className="text-slate-400 text-sm mt-2">{t('dailyTimesheet.clickCalculateButton')}</p>
          </div>
        ) : report.rows.length === 0 ? (
          <div className="py-20 text-center">
            <AlertTriangle size={48} className="mx-auto text-slate-200 mb-4" />
            <h3 className="text-slate-400 font-bold uppercase text-xs tracking-wider">{t('dailyTimesheet.noRecordsFound')}</h3>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left min-w-[1200px]">
              <thead>
                <tr className="text-[10px] font-black text-slate-400 uppercase tracking-widest bg-slate-50">
                  <th className="py-4 px-4 sticky left-0 bg-amber-100 text-amber-800">{t('dailyTimesheet.cardNo')}</th>
                  <th className="py-4 px-4 bg-amber-100 text-amber-800">{t('dailyTimesheet.name')}</th>
                  <th className="py-4 px-4">{t('dailyTimesheet.group')}</th>
                  <th className="py-4 px-4">{t('dailyTimesheet.section')}</th>
                  <th className="py-4 px-4 bg-indigo-100 text-indigo-800">{t('dailyTimesheet.shift')}</th>
                  <th className="py-4 px-4 bg-amber-100 text-amber-800">{t('dailyTimesheet.date')}</th>
                  <th className="py-4 px-4 bg-amber-100 text-amber-800 text-center">{t('dailyTimesheet.entry')}</th>
                  <th className="py-4 px-4 bg-amber-100 text-amber-800 text-center">{t('dailyTimesheet.exit')}</th>
                  <th className="py-4 px-4 text-center">{t('dailyTimesheet.entry2')}</th>
                  <th className="py-4 px-4 text-center">{t('dailyTimesheet.exit2')}</th>
                  <th className="py-4 px-4 text-center">Giriş 3</th>
                  <th className="py-4 px-4 text-center">Çıkış 3</th>
                  <th className="py-4 px-4 text-center">Giriş 4</th>
                  <th className="py-4 px-4 text-center">Çıkış 4</th>
                  {/* 2026-08-14 (kullanici talebi): O gun FIILEN calisilan sure.
                      Kullanici GEC/ERK/EKS'i gorup toplam calismayi goremiyordu. */}
                  <th className="py-4 px-4 text-center text-green-700 bg-green-50">ÇALIŞMA</th>
                  <th className="py-4 px-4 text-center text-red-600">{t('dailyTimesheet.absenceShort')}</th>
                  <th className="py-4 px-4 text-center text-amber-600">{t('dailyTimesheet.lateShort')}</th>
                  <th className="py-4 px-4 text-center text-orange-600">{t('dailyTimesheet.earlyShort')}</th>
                  <th className="py-4 px-4 text-center text-blue-600">{t('dailyTimesheet.deficitShort')}</th>
                  <th className="py-4 px-4 text-center text-purple-600">MESAİ</th>
                  <th className="py-4 px-4">{t('dailyTimesheet.description')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {report.rows.map((row, idx) => (
                  <tr key={idx} className={`hover:bg-slate-50/50 ${getRowClass(row)}`}>
                    <td className="py-3 px-4 font-mono text-sm font-bold text-slate-700 sticky left-0 bg-inherit">{row.kartNo}</td>
                    <td className="py-3 px-4">
                      <span className="font-bold text-slate-800">{row.isim}</span>
                    </td>
                    <td className="py-3 px-4 text-sm text-slate-600">{row.grup}</td>
                    <td className="py-3 px-4 text-sm text-slate-600">{row.bolum}</td>
                    <td className="py-3 px-4">
                      {row.vardiya ? (
                        <span className="px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded-md text-xs font-bold">{row.vardiya}</span>
                      ) : (
                        <span className="text-slate-300 text-xs">-</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-sm font-medium">{formatDate(row.tarih)}</td>
                    <td className="py-3 px-4 text-center">
                      <span className={`font-mono font-bold ${row.giris1 ? 'text-green-600' : 'text-slate-300'}`}>
                        {formatTime(row.giris1)}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span className={`font-mono font-bold ${row.cikis1 ? 'text-blue-600' : 'text-slate-300'}`}>
                        {formatTime(row.cikis1)}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-center text-slate-500 font-mono text-sm">{formatTime(row.giris2)}</td>
                    <td className="py-3 px-4 text-center text-slate-500 font-mono text-sm">{formatTime(row.cikis2)}</td>
                    <td className="py-3 px-4 text-center text-slate-500 font-mono text-sm">{formatTime(row.giris3)}</td>
                    <td className="py-3 px-4 text-center text-slate-500 font-mono text-sm">{formatTime(row.cikis3)}</td>
                    <td className="py-3 px-4 text-center text-slate-500 font-mono text-sm">{formatTime(row.giris4)}</td>
                    <td className="py-3 px-4 text-center text-slate-500 font-mono text-sm">{formatTime(row.cikis4)}</td>
                    <td className="py-3 px-4 text-center bg-green-50/40">
                      {row.normalCalisma && row.normalCalisma !== '00:00:00' ? (
                        <span className="font-bold text-green-700">{formatTime(row.normalCalisma)}</span>
                      ) : (
                        <span className="text-slate-300">-</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-center">
                      {row.devamsizlik && row.devamsizlik !== '00:00:00' && (
                        <span className="font-bold text-red-600">{formatTime(row.devamsizlik)}</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-center">
                      {row.gecKalma && row.gecKalma !== '00:00:00' && (
                        <span className="font-bold text-amber-600">{formatTime(row.gecKalma)}</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-center">
                      {row.erkenCikma && row.erkenCikma !== '00:00:00' && (
                        <span className="font-bold text-orange-600">{formatTime(row.erkenCikma)}</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-center">
                      {row.eksikSure && row.eksikSure !== '00:00:00' && (
                        <span className="font-bold text-blue-600">{formatTime(row.eksikSure)}</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-center">
                      {(() => {
                        const m50 = row.fazlaMesai50 && row.fazlaMesai50 !== '00:00:00' ? row.fazlaMesai50 : null;
                        const m100 = row.fazlaMesai100 && row.fazlaMesai100 !== '00:00:00' ? row.fazlaMesai100 : null;
                        if (!m50 && !m100) return null;
                        const toMin = (s: string) => { const [h, m] = s.split(':'); return (+h) * 60 + (+m); };
                        const total = (m50 ? toMin(m50) : 0) + (m100 ? toMin(m100) : 0);
                        const hh = Math.floor(total / 60).toString().padStart(2, '0');
                        const mm = (total % 60).toString().padStart(2, '0');
                        return <span className="font-bold text-purple-600">{hh}:{mm}</span>;
                      })()}
                    </td>
                    <td className="py-3 px-4 text-sm text-slate-600 max-w-[200px] truncate" title={row.aciklama}>
                      {row.aciklama}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default DailyTimesheet;
